/**
 * Socket.IO client for real-time communication.
 * Handles WebSocket connections, message emission, and acknowledgements.
 */
import { checkFactoryVersion, resetFactoryVersionCheck } from 'services/version/checkFactoryVersion';
import { showOSNotification } from 'services/notifications/osNotification';
import { handleSocketException } from 'services/session/sessionGuard';
import { getLoginState } from 'services/authentication/loginState';
import { getToken } from 'services/authentication/tokenManagement';
import { processDirective } from 'services/processDirective';
import { tmxToast } from 'services/notifications/tmxToast';
import { tools, version } from 'tods-competition-factory';
import { isFunction, isObject } from 'functions/typeOf';
import { version as tmxVersion } from 'config/version';
import { serverConfig } from 'config/serverConfig';
import { debugConfig } from 'config/debugConfig';
import { io } from 'socket.io-client';
import { t } from 'i18n';
import {
  setChatSendFn,
  setChatGapFn,
  receiveMessage,
  receiveAccepted,
  receiveRejected,
  receiveHistory,
  setOnlineCount,
} from 'services/chat/chatService';
import {
  setAdminMonitorFns,
  receiveAdminChatFeed,
  receiveAdminChatHistory,
  rejoinChatMonitorIfActive,
} from 'services/chat/adminChatService';

// types
import type { ServerAck } from 'types/services';

// constants
import {
  CLIENT_ERROR,
  SEND_KEY,
  TMX_DIRECTIVE,
  TMX_MESSAGE,
  JOIN_TOURNAMENT,
  LEAVE_TOURNAMENT,
} from 'constants/comsConstants';

const slog = (...args: any[]) => debugConfig.get().socketLog && console.log(...args);

function getAuthorization(): { authorization: string } | undefined {
  const token = getToken();
  if (!token) return undefined;
  const authorization = `Bearer ${token}`;
  return { authorization };
}

const oi: any = {
  timestampOffset: 0,
  socket: undefined,
};

/** True after a disconnect event until cleared by `clearDisconnectFlag()`. */
let disconnectedSinceLastNav = false;

/** Listeners invoked after the socket reconnects (not on the first connect).
 * Used by the staleness guard to check whether mutations were missed during the
 * offline window. Registered via `onSocketReconnect`. */
const reconnectListeners: Array<() => void> = [];

/** Register a callback to run after the socket reconnects following a drop.
 * Does not fire on the initial connection. */
export function onSocketReconnect(listener: () => void): void {
  reconnectListeners.push(listener);
}

const ackRequests: Record<string, (ack: ServerAck) => void> = {};
const ackTimeouts: Record<string, ReturnType<typeof setTimeout>> = {};
const socketQueue: any[] = [];

let mutationListener: ((data: any) => void) | null = null;
let currentTournamentRoom: string | undefined;

function tmxMessage(data: any): void {
  slog('[socket] tmxMessage:', { data });
}

/** Register a callback for remote tournament mutations broadcast by the server. */
export function onTournamentMutation(callback: ((data: any) => void) | null): void {
  mutationListener = callback;
}

let facilityScheduleListener: ((data: any) => void) | null = null;

/** Register a callback for opaque `facilityScheduleChanged` events — a linked facility peer's schedule
 * moved on the server. The event carries only `{ venueIds, changedAt }` (no tournament/matchUp detail);
 * the consumer re-fetches its reserved-cell projection, which re-gates server-side. */
export function onFacilityScheduleChanged(callback: ((data: any) => void) | null): void {
  facilityScheduleListener = callback;
}

function handleFacilityScheduleChanged(data: any): void {
  slog('[socket] received facilityScheduleChanged — venues:', data?.venueIds);
  facilityScheduleListener?.(data);
}

/** e2e/dev only: deliver a synthetic `facilityScheduleChanged` to the registered listener, to exercise
 * the client re-fetch path without a live linked-peer mutation on the server. */
export function simulateFacilityScheduleChanged(data: any): void {
  handleFacilityScheduleChanged(data);
}

function handleTournamentMutation(data: any): void {
  slog(
    '[socket] received tournamentMutation — methods:',
    data?.methods?.length,
    'tournaments:',
    data?.tournamentIds,
    'from:',
    data?.userId,
  );
  if (mutationListener) {
    mutationListener(data);
  } else {
    console.warn('[socket] tournamentMutation received but no listener registered');
  }
}

export function connectSocket(callback?: () => void): void {
  const connectionOptions: any = {
    // `auth` is a function so socket.io-client re-invokes it before every
    // (re)connect, always presenting the *current* token. The server's
    // SocketGuard prefers handshake.auth.token over the Authorization header
    // precisely because the header is baked in at initial connect and goes
    // stale on the first reconnect after a token refresh — which is what left
    // an expired-then-refreshed /tmx session silently rejected on every
    // executionQueue (the /hiveid socket clients got this fix on 2026-06-01;
    // /tmx did not until now). extraHeaders is kept for the polling handshake.
    auth: (cb: (data: { token?: string }) => void) => cb({ token: getToken() ?? undefined }),
    transportOptions: { polling: { extraHeaders: getAuthorization() } },
    // jim-tennis-deploy (50b60c51): force polling-only. Caddy fronts the
    // factory-server on HTTP/2, which doesn't support the standard
    // WebSocket Upgrade handshake — engine.io's WebSocket negotiation
    // returns 400 and the socket hangs without acks. Pinning to polling
    // sidesteps the upgrade entirely; mutations + ack callbacks travel
    // over long-polling HTTP requests, which Caddy proxies cleanly.
    transports: ['polling'],
    'force new connection': true,
    reconnectionDelay: 1000,
    // A NUMBER, not the string 'Infinity'. socket.io compares
    // `attempts >= reconnectionAttempts`; against a string that comparison is
    // always false, so the old value never gave up only by accident.
    reconnectionAttempts: Infinity,
    timeout: 20000,
  };
  if (oi.socket) {
    slog('[socket] connectSocket called but socket already exists (connected=%s)', oi.socket.connected);
  } else {
    // jim-tennis-deploy: extract ORIGIN only from socketPath. process.env.SERVER
    // (and therefore serverConfig.socketPath) is "https://jim.tennis/api/courthive"
    // on this Caddy-proxied deployment — Caddy strips the prefix when forwarding
    // REST API calls to factory-server. For socket.io we connect via Caddy's
    // /socket.io/* route directly, so we must NOT carry the /api/courthive prefix
    // into the namespace string — otherwise engine.io tries to open namespace
    // "/api/courthive/tmx" which doesn't exist on the gateway (it lives at /tmx).
    // (Restores 50b60c51's intent after the upstream rebase regressed it.)
    const rawSocketPath = serverConfig.get().socketPath || globalThis.location.origin;
    let socketOrigin = globalThis.location.origin;
    try {
      socketOrigin = new URL(rawSocketPath).origin;
    } catch {
      // socketPath might be a bare path (legacy) — fall back to window origin
    }
    const connectionString = `${socketOrigin}/tmx`;
    slog('[socket] connecting to', connectionString);
    oi.socket = io(connectionString, connectionOptions);
    oi.socket.on('ack', receiveAcknowledgement);
    oi.socket.on(TMX_MESSAGE, tmxMessage);
    oi.socket.on(TMX_DIRECTIVE, processDirective);
    oi.socket.on('tournamentMutation', handleTournamentMutation);
    oi.socket.on('facilityScheduleChanged', handleFacilityScheduleChanged);
    oi.socket.on('chatMessage', receiveMessage);
    oi.socket.on('chatAccepted', receiveAccepted);
    oi.socket.on('chatRejected', receiveRejected);
    oi.socket.on('chatHistory', receiveHistory);
    oi.socket.on('roomPresence', setOnlineCount);
    oi.socket.on('adminChatFeed', receiveAdminChatFeed);
    oi.socket.on('adminChatHistory', receiveAdminChatHistory);
    oi.socket.on('connect', () => connectionEvent(callback));

    setChatSendFn((data: any) => socketEmit('chatMessage', data));
    setChatGapFn((data: any) => socketEmit('chatSince', data));
    setAdminMonitorFns({
      join: () => socketEmit('joinChatMonitor', {}),
      leave: () => socketEmit('leaveChatMonitor', {}),
      reply: (data: any) => socketEmit('adminChatReply', data),
    });
    oi.socket.on('disconnect', (reason: string) => {
      slog('[socket] disconnected — reason:', reason);
      disconnectedSinceLastNav = true;
      resetFactoryVersionCheck();
      showOSNotification({ title: 'TMX', body: 'Server connection lost' });
    });
    oi.socket.on('exception', (data: any) => {
      // The server's SocketGuard emits `exception` when it rejects a message —
      // most commonly for an expired/absent/wrong-audience token. Route those
      // to the session guard so the user gets a "log in again" banner and their
      // edit is preserved, instead of the message silently dying and surfacing
      // 10s later as a misleading "Server not responding". Non-auth exceptions
      // still surface here rather than being swallowed (A2).
      const handledAsAuth = handleSocketException(data);
      if (!handledAsAuth) console.warn('[socket] server exception:', data);
    });
    oi.socket.on('timestamp', (data: any) => (oi.timestampOffset = Date.now() - data.timestamp));
    oi.socket.on('connect_error', (data: any) => {
      slog('[socket] connect_error:', data?.message ?? data);
      // DO NOT tear the socket down here.
      //
      // This handler used to call `disconnectSocket()`. In socket.io-client an
      // explicit `disconnect()` CANCELS automatic reconnection — the manager
      // will not retry after it — and `disconnectSocket` then deletes the
      // socket object outright. So a single transient `connect_error`
      // permanently disarmed reconnection, which is exactly what happens on a
      // back-forward-cache restore: the browser kills the transport while the
      // page is frozen, the first reconnect attempt errors, and the client then
      // sat dead for as long as the operator stayed on the page.
      //
      // A connect_error is transient by definition — socket.io is already
      // scheduling the next attempt. Auth rejections do NOT arrive here; the
      // server's SocketGuard emits `exception`, handled above.
      notifyConnectionTrouble();
    });
  }
}

/**
 * A `connect_error` fires once per retry attempt, and socket.io retries every
 * second — so toasting per event produced a wall of identical danger toasts.
 * One toast per trouble window, re-armed by the next successful connect.
 */
let connectionTroubleNotified = false;

function notifyConnectionTrouble(): void {
  if (connectionTroubleNotified) return;
  connectionTroubleNotified = true;
  tmxToast({ message: t('toasts.connectionError'), intent: 'is-danger' });
}

/**
 * True only when there is a LIVE connection.
 *
 * Previously `!!oi.socket`, which is truthy for a socket object that exists and
 * is disconnected — so the main menu offered "Disconnect" on a dead socket, the
 * settings panel displayed "Connected", and `requestTournamentRecord` emitted
 * into the void instead of telling the operator they are offline. All four
 * callers want live connectivity.
 */
export function connected(): boolean {
  return !!oi.socket?.connected;
}

/** True when a socket object exists at all, connected or not (internal lifecycle checks). */
export function socketExists(): boolean {
  return !!oi.socket;
}

export function disconnectSocket(): void {
  slog('[socket] disconnectSocket called');
  // jim-tennis-deploy (50b60c51): immediate cleanup of listeners + reference
  // to avoid stale-listener leaks across reconnects.
  if (oi.socket) {
    oi.socket.removeAllListeners();
    oi.socket.disconnect();
    oi.socket = undefined;
  }
}

/**
 * Force a fresh handshake so the `auth` callback re-presents the current token.
 * Used after a silent refresh / re-login to shed a socket that is still holding
 * a stale (now-expired) token. Reconnecting fires `connectionEvent`, which — on
 * a post-drop reconnect — runs the registered reconnect listeners (the session
 * guard replays any preserved edits there).
 */
export function reconnectSocket(): void {
  if (oi.socket) {
    oi.socket.disconnect();
    oi.socket.connect();
  } else {
    connectSocket();
  }
}

/**
 * Reconnect the socket if the user is logged in but the connection is down.
 * Returns true if a reconnect was initiated.
 *
 * ⚠️ Must reconnect the EXISTING socket when there is one. This used to call
 * `connectSocket()` unconditionally — but `connectSocket` early-returns when
 * `oi.socket` is truthy, so with a disconnected-but-not-deleted socket the whole
 * call was a silent no-op. That made the one caller (the router) unreliable too:
 * whether navigation recovered the connection depended on whether the socket
 * object happened to have been deleted yet.
 */
export function ensureConnected(): boolean {
  if (oi.socket?.connected) return false;
  const state = getLoginState();
  if (!state) return false;

  if (oi.socket) {
    slog('[socket] ensureConnected — re-opening existing socket (disconnected)');
    // Re-arms a manager that an explicit disconnect had stopped.
    oi.socket.connect();
    return true;
  }

  slog('[socket] ensureConnected — no socket, connecting fresh');
  connectSocket();
  return true;
}

/** True if a disconnect occurred since the last call to `clearDisconnectFlag()`. */
export function hadDisconnect(): boolean {
  return disconnectedSinceLastNav;
}

export function clearDisconnectFlag(): void {
  disconnectedSinceLastNav = false;
}

export function emitTmx({ data, ackCallback }: { data: any; ackCallback?: (ack: ServerAck) => void }): void {
  const state = getLoginState();
  const { email: userId } = state || {};
  const messageType = data.type ?? 'tmx';

  const action = () => {
    if (ackCallback && isFunction(ackCallback)) {
      const ackId = tools.UUID();
      if (data.payload) Object.assign(data.payload, { ackId });

      requestAcknowledgement({ ackId, callback: ackCallback });
    }

    const timestamp = oi.timestampOffset ? Date.now() + oi.timestampOffset : Date.now();
    Object.assign(data.payload || data, {
      factoryVersion: version(),
      tmxVersion,
      timestamp,
      userId,
    });

    socketEmit(messageType, data);
  };

  if (oi.socket?.connected) {
    action();
  } else {
    try {
      connectSocket(action);
    } catch {
      // Socket action failed - queue message for retry
      socketQueue.push({ header: messageType, data, ackCallback });
    }
  }
}

function socketEmit(msg: string, data: any): void {
  if (oi.socket.connected) {
    slog('[socket] emit:', msg, data?.type ?? '');
    oi.socket.emit(msg, data);
  } else {
    slog('[socket] emit skipped (not connected) — msg:', msg);
  }
}

function connectionEvent(callback?: () => void): void {
  slog('[socket] connected — id:', oi.socket?.id);
  // Re-arm the trouble toast so the NEXT outage is announced once more.
  connectionTroubleNotified = false;
  // Capture before anything clears the flag: true only when this `connect` is a
  // reconnect after a prior drop (the first connect leaves the flag false).
  const reconnected = disconnectedSinceLastNav;
  emitTmx({ data: { type: 'timestamp' } });
  while (socketQueue.length) {
    const message = socketQueue.pop();
    socketEmit(message.header, message.data);
  }

  // Re-join tournament room after reconnect (room membership is lost on disconnect)
  if (currentTournamentRoom) {
    slog('[socket] re-joining tournament room after reconnect:', currentTournamentRoom);
    oi.socket.emit(JOIN_TOURNAMENT, { tournamentId: currentTournamentRoom });
  }

  // Re-join the super-admin chat monitor room after reconnect if it was open.
  rejoinChatMonitorIfActive();

  void checkFactoryVersion();

  // After a reconnect, notify listeners (staleness guard) so the client can
  // detect mutations broadcast while it was offline (Socket.IO has no replay).
  if (reconnected) {
    for (const listener of reconnectListeners) {
      try {
        listener();
      } catch (err) {
        slog('[socket] reconnect listener error:', err);
      }
    }
  }

  if (isFunction(callback)) callback();
}

const MAX_PENDING_ACKS = 100;

function requestAcknowledgement({
  ackId,
  uuid,
  callback,
}: {
  ackId?: string;
  uuid?: string;
  callback: (ack: ServerAck) => void;
}): void {
  // Prevent unbounded growth — purge oldest entries if limit reached
  const keys = Object.keys(ackRequests);
  if (keys.length >= MAX_PENDING_ACKS) {
    const toRemove = keys.slice(0, keys.length - MAX_PENDING_ACKS + 1);
    for (const key of toRemove) {
      clearTimeout(ackTimeouts[key]);
      delete ackRequests[key];
      delete ackTimeouts[key];
    }
    slog('[socket] purged', toRemove.length, 'stale ack requests');
  }

  const cleanup = () => {
    if (ackId) {
      delete ackRequests[ackId];
      delete ackTimeouts[ackId];
    }
    if (uuid) {
      delete ackRequests[uuid];
      delete ackTimeouts[uuid];
    }
  };

  const timeoutMs = (serverConfig.get().serverTimeout ?? 10000) * 3;
  const timerId = setTimeout(cleanup, timeoutMs);

  const wrappedCallback = (ack: any) => {
    cleanup();
    clearTimeout(timerId);
    try {
      callback(ack);
    } catch (err) {
      console.error('[socket] ack callback error:', err);
    }
  };

  if (ackId) {
    ackRequests[ackId] = wrappedCallback;
    ackTimeouts[ackId] = timerId;
  }
  if (uuid) {
    ackRequests[uuid] = wrappedCallback;
    ackTimeouts[uuid] = timerId;
  }
}

function receiveAcknowledgement(ack: ServerAck): void {
  // Prefer ackId; fall back to uuid. Only fire once.
  const key = (ack.ackId && ackRequests[ack.ackId] && ack.ackId) || (ack.uuid && ackRequests[ack.uuid] && ack.uuid);
  if (key) ackRequests[key](ack);
}

/** Join a tournament room to receive mutation broadcasts from other clients. */
export function joinTournamentRoom(tournamentId: string): void {
  currentTournamentRoom = tournamentId;
  if (!tournamentId || !oi.socket?.connected) {
    slog('[socket] joinTournamentRoom skipped — tournamentId=%s, connected=%s', tournamentId, oi.socket?.connected);
    return;
  }
  slog('[socket] joining room:', tournamentId);
  oi.socket.emit(JOIN_TOURNAMENT, { tournamentId });
}

/** Leave a tournament room to stop receiving mutation broadcasts. */
export function leaveTournamentRoom(tournamentId: string): void {
  if (currentTournamentRoom === tournamentId) currentTournamentRoom = undefined;
  if (!tournamentId || !oi.socket?.connected) {
    slog('[socket] leaveTournamentRoom skipped — tournamentId=%s, connected=%s', tournamentId, oi.socket?.connected);
    return;
  }
  slog('[socket] leaving room:', tournamentId);
  oi.socket.emit(LEAVE_TOURNAMENT, { tournamentId });
}

export function logError(err: any): void {
  if (!err) return;
  const stack = err.stack?.toString();
  const errorMessage = isObject(err) ? JSON.stringify(err) : err;
  const payload = { stack, error: errorMessage };
  emitTmx({ data: { action: CLIENT_ERROR, payload } });
}

let keyQueue: string[] = [];
export function queueKey(key: string): void {
  keyQueue.push(key);
}
const sendKey = (key: string) => emitTmx({ data: { action: SEND_KEY, payload: { key } } });
export function sendQueuedKeys(): void {
  keyQueue.forEach(sendKey);
  keyQueue = [];
}
